import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, CircularProgress, Stack, Chip, ThemeProvider, createTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import useMetaTags from '../../hooks/useMetaTags';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import baseTheme from '../../theme/theme';

// Create a custom theme for this page that uses green instead of beige
const cursosTheme = createTheme({
  ...baseTheme,
  palette: {
    ...baseTheme.palette,
    primary: {
      main: '#455b40',
      light: '#5a7654',
      dark: '#34442f',
      contrastText: '#FFFFFF'
    }
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(69,91,64,0.2),0px 4px 5px 0px rgba(69,91,64,0.14),0px 1px 10px 0px rgba(69,91,64,0.12)',
    '0px 3px 5px -1px rgba(69,91,64,0.2),0px 5px 8px 0px rgba(69,91,64,0.14),0px 1px 14px 0px rgba(69,91,64,0.12)',
    '0px 3px 5px -1px rgba(69,91,64,0.2),0px 6px 10px 0px rgba(69,91,64,0.14),0px 1px 18px 0px rgba(69,91,64,0.12)',
    '0px 4px 5px -2px rgba(69,91,64,0.2),0px 7px 10px 1px rgba(69,91,64,0.14),0px 2px 16px 1px rgba(69,91,64,0.12)',
    '0px 5px 5px -3px rgba(69,91,64,0.2),0px 8px 10px 1px rgba(69,91,64,0.14),0px 3px 14px 2px rgba(69,91,64,0.12)',
    '0px 5px 6px -3px rgba(69,91,64,0.2),0px 9px 12px 1px rgba(69,91,64,0.14),0px 3px 16px 2px rgba(69,91,64,0.12)',
    '0px 6px 6px -3px rgba(69,91,64,0.2),0px 10px 14px 1px rgba(69,91,64,0.14),0px 4px 18px 3px rgba(69,91,64,0.12)',
    '0px 6px 7px -4px rgba(69,91,64,0.2),0px 11px 15px 1px rgba(69,91,64,0.14),0px 4px 20px 3px rgba(69,91,64,0.12)',
    '0px 7px 8px -4px rgba(69,91,64,0.2),0px 12px 17px 2px rgba(69,91,64,0.14),0px 5px 22px 4px rgba(69,91,64,0.12)',
    '0px 7px 8px -4px rgba(69,91,64,0.2),0px 13px 19px 2px rgba(69,91,64,0.14),0px 5px 24px 4px rgba(69,91,64,0.12)',
    '0px 7px 9px -4px rgba(69,91,64,0.2),0px 14px 21px 2px rgba(69,91,64,0.14),0px 5px 26px 4px rgba(69,91,64,0.12)',
    '0px 8px 9px -5px rgba(69,91,64,0.2),0px 15px 22px 2px rgba(69,91,64,0.14),0px 6px 28px 5px rgba(69,91,64,0.12)',
    '0px 8px 10px -5px rgba(69,91,64,0.2),0px 16px 24px 2px rgba(69,91,64,0.14),0px 6px 30px 5px rgba(69,91,64,0.12)',
    '0px 8px 11px -5px rgba(69,91,64,0.2),0px 17px 26px 2px rgba(69,91,64,0.14),0px 6px 32px 5px rgba(69,91,64,0.12)',
    '0px 9px 11px -5px rgba(69,91,64,0.2),0px 18px 28px 2px rgba(69,91,64,0.14),0px 7px 34px 6px rgba(69,91,64,0.12)',
    '0px 9px 12px -6px rgba(69,91,64,0.2),0px 19px 29px 2px rgba(69,91,64,0.14),0px 7px 36px 6px rgba(69,91,64,0.12)',
    '0px 10px 13px -6px rgba(69,91,64,0.2),0px 20px 31px 3px rgba(69,91,64,0.14),0px 8px 38px 7px rgba(69,91,64,0.12)',
    '0px 10px 13px -6px rgba(69,91,64,0.2),0px 21px 33px 3px rgba(69,91,64,0.14),0px 8px 40px 7px rgba(69,91,64,0.12)',
    '0px 10px 14px -6px rgba(69,91,64,0.2),0px 22px 35px 3px rgba(69,91,64,0.14),0px 8px 42px 7px rgba(69,91,64,0.12)',
    '0px 11px 14px -7px rgba(69,91,64,0.2),0px 23px 36px 3px rgba(69,91,64,0.14),0px 9px 44px 8px rgba(69,91,64,0.12)',
    '0px 11px 15px -7px rgba(69,91,64,0.2),0px 24px 38px 3px rgba(69,91,64,0.14),0px 9px 46px 8px rgba(69,91,64,0.12)'
  ] as any,
  components: {
    ...baseTheme.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(69, 91, 64, 0.1)'
        }
      }
    }
  }
});

interface Course {
  id: number;
  slug: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  featured_media?: number;
  acf?: {
    [key: string]: any;
    count_down_start_date?: string;
    count_down_start_time?: string;
    day_1_date?: string;
    day_2_date?: string;
    day_3_date?: string;
    image_url_home?: string;
  };
  date: string;
}

const CoursesSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  backgroundColor: theme.palette.background.default,
  minHeight: '600px',
}));

const CourseCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  overflow: 'hidden',
  transition: 'all 0.4s ease',
  border: `1px solid ${theme.palette.primary.light}30`,
  boxShadow: '0 4px 20px rgba(69, 91, 64, 0.1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(69, 91, 64, 0.25)',
    borderColor: theme.palette.primary.main,
  },
}));

const CourseImageContainer = styled(Box)(({ theme }) => ({
  height: '200px',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f5f5f5',
}));

const CourseImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center 20%',
  transition: 'transform 0.4s ease',
}));

const CourseContent = styled(CardContent)(({ theme }) => ({
  padding: '30px',
}));

const CourseTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontWeight: 700,
  fontSize: '24px',
  color: theme.palette.text.primary,
  marginBottom: '16px',
  lineHeight: 1.3,
  minHeight: '62px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const CourseDescription = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '15px',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  marginBottom: '24px',
  minHeight: '80px',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const CourseMeta = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '20px',
  marginBottom: '24px',
  flexWrap: 'wrap',
}));

const MetaItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  '& svg': {
    fontSize: '16px',
    color: theme.palette.primary.main,
  },
  '& span': {
    fontSize: '13px',
    color: theme.palette.text.secondary,
    fontFamily: '"Lora", serif',
  },
}));

const CourseStatus = styled(Chip)(({ theme }) => ({
  backgroundColor: `${theme.palette.primary.light}20`,
  color: theme.palette.primary.dark,
  fontWeight: 600,
  fontSize: '12px',
  height: '24px',
  fontFamily: '"Lora", serif',
  '& .MuiChip-label': {
    padding: '0 10px',
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 700,
  padding: '12px 28px',
  borderRadius: '25px',
  fontSize: '15px',
  fontFamily: '"Lora", serif',
  border: `2px solid ${theme.palette.primary.main}`,
  position: 'relative',
  overflow: 'hidden',
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    position: 'relative',
    zIndex: 2,
    transition: 'transform 0.3s ease',
  },
  '& span': {
    position: 'relative',
    zIndex: 2,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',
    transition: 'left 0.4s ease',
    zIndex: 1,
  },
  '&::after': {
    content: '"Ver Detalles →"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: '15px',
    fontFamily: '"Lora", serif',
    zIndex: 3,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    whiteSpace: 'nowrap',
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
    '&::before': {
      left: 0,
    },
    '&::after': {
      opacity: 1,
    },
    '& span': {
      opacity: 0,
    },
    '& .MuiButton-endIcon': {
      transform: 'translateX(4px)',
    },
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
}));

const ErrorMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '40px',
  backgroundColor: `${theme.palette.error.light}10`,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.error.light}30`,
  '& .MuiTypography-root': {
    color: theme.palette.error.main,
    fontFamily: '"Lora", serif',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '60px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '42px',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: '16px',
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    backgroundColor: theme.palette.primary.main,
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Lora", serif',
  fontSize: '18px',
  color: theme.palette.text.secondary,
  maxWidth: '600px',
  margin: '0 auto',
  lineHeight: 1.7,
  marginTop: '30px',
}));

const Cursos: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Update meta tags for courses page
  useMetaTags({
    title: 'Cursos de Agricultura Ecológica - EKKOFOOD',
    description: 'Descubre nuestros cursos de agricultura ecológica con expertos internacionales. Aprende permacultura, agricultura regenerativa y técnicas sostenibles en nuestra finca de Bétera, Valencia.',
    image: 'https://ekkofood.com/course.png',
    url: 'https://ekkofood.com/cursos',
    type: 'website'
  });

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await fetch(`https://thomass362.sg-host.com/wp-json/wp/v2/cursos?per_page=100&acf_format=standard&_=${timestamp}`);
        if (!response.ok) {
          throw new Error('Failed to fetch courses');
        }
        const data = await response.json();
        // Sort by ID in ascending order (lowest first)
        const sortedCourses = data.sort((a: Course, b: Course) => a.id - b.id);
        setCourses(sortedCourses);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const formatDate = (dateString: string) => {
    let date = new Date(dateString);
    
    // Handle ACF date format (YYYYMMDD) if needed
    if (dateString && dateString.length === 8 && !dateString.includes('-')) {
      // Convert YYYYMMDD to YYYY-MM-DD
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      date = new Date(`${year}-${month}-${day}`);
    }
    
    if (isNaN(date.getTime())) {
      return 'Fecha no disponible';
    }
    
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getWeeksFromNow = (targetDate: string): number => {
    const now = new Date();
    let target = new Date(targetDate);
    
    // Handle ACF date format (YYYYMMDD) if needed
    if (targetDate && targetDate.length === 8 && !targetDate.includes('-')) {
      // Convert YYYYMMDD to YYYY-MM-DD
      const year = targetDate.substring(0, 4);
      const month = targetDate.substring(4, 6);
      const day = targetDate.substring(6, 8);
      target = new Date(`${year}-${month}-${day}`);
    }
    
    console.log('Target date string:', targetDate);
    console.log('Parsed target date:', target);
    console.log('Is valid date:', !isNaN(target.getTime()));
    
    if (isNaN(target.getTime())) {
      return 0; // Return 0 if invalid date
    }
    
    const diffTime = target.getTime() - now.getTime();
    const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    return Math.max(0, diffWeeks); // Don't show negative weeks
  };

  const getCourseStartDate = (course: Course): string => {
    // Priority: count_down_start_date > day_1_date > course.date
    console.log('Course ACF data:', course.acf);
    
    if (course.acf?.count_down_start_date) {
      console.log('Using count_down_start_date:', course.acf.count_down_start_date);
      return course.acf.count_down_start_date;
    }
    if (course.acf?.day_1_date) {
      console.log('Using day_1_date:', course.acf.day_1_date);
      return course.acf.day_1_date;
    }
    console.log('Using course.date:', course.date);
    return course.date;
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <ThemeProvider theme={cursosTheme}>
      <PageHero
        title="Nuestros Cursos"
        subtitle="Aprende agricultura sostenible con expertos"
       backgroundImage="/organic_farm.png"
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: 'Inicio', link: '/' },
          { label: 'Cursos', link: '/cursos' },
        ]}
        height="400px"
        overlayOpacity={0.5}
      />

      <CoursesSection>
        <Container maxWidth="lg">
          <SectionHeader>
            <SectionTitle variant="h2">
              Cursos Disponibles
            </SectionTitle>
            <SectionSubtitle variant="body1">
              Explora nuestra selección de cursos especializados en agricultura orgánica, 
              diseñados para todos los niveles de experiencia
            </SectionSubtitle>
          </SectionHeader>

          {loading && (
            <LoadingContainer>
              <CircularProgress 
                size={60} 
                sx={{ color: 'primary.main' }}
              />
            </LoadingContainer>
          )}

          {error && (
            <ErrorMessage>
              <Typography variant="h6" gutterBottom>
                Error al cargar los cursos
              </Typography>
              <Typography variant="body2">
                {error}
              </Typography>
            </ErrorMessage>
          )}

          {!loading && !error && courses.length === 0 && (
            <ErrorMessage>
              <Typography variant="h6">
                No hay cursos disponibles en este momento
              </Typography>
            </ErrorMessage>
          )}

          {!loading && !error && courses.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)'
                },
                gap: 4
              }}
            >
              {courses.map((course, index) => (
                <CourseCard key={course.id}>
                  <CourseImageContainer>
                    <CourseImage 
                      src={course.acf?.image_url_home || '/course.png'} 
                      alt={stripHtml(course.title.rendered)}
                      onError={(e) => {
                        e.currentTarget.src = '/course.png';
                      }}
                    />
                  </CourseImageContainer>
                  <CourseContent>
                    <CourseTitle>
                      {stripHtml(course.title.rendered)}
                    </CourseTitle>
                    <CourseDescription>
                      {course.excerpt ? stripHtml(course.excerpt.rendered) : stripHtml(course.content.rendered).substring(0, 150) + '...'}
                    </CourseDescription>
                    <CourseMeta>
                      <MetaItem>
                        <CalendarTodayIcon />
                        <span>{formatDate(getCourseStartDate(course))}</span>
                      </MetaItem>
                      <MetaItem>
                        <AccessTimeIcon />
                        <span>
                          {(() => {
                            const courseStartDate = getCourseStartDate(course);
                            const weeks = getWeeksFromNow(courseStartDate);
                            
                            // Handle invalid dates
                            if (!courseStartDate || courseStartDate === 'Fecha no disponible') {
                              return 'Próximamente';
                            }
                            
                            if (weeks === 0) return 'Inicia esta semana';
                            if (weeks === 1) return 'En 1 semana';
                            return `En ${weeks} semanas`;
                          })()}
                        </span>
                      </MetaItem>
                    </CourseMeta>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <CourseStatus label="Disponible" />
                      <CTAButton 
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/cursos/${course.slug}`)}
                      >
                        <span>Ver Detalles</span>
                      </CTAButton>
                    </Stack>
                  </CourseContent>
                </CourseCard>
              ))}
            </Box>
          )}
        </Container>
      </CoursesSection>

      <Footer />
    </ThemeProvider>
  );
};

export default Cursos;